import React, { useState, useEffect } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ShoppingBag, Star, Truck, CreditCard, Crown, Bell, BellRing, Megaphone } from 'lucide-react';
import { useUnifiedUser } from '@/hooks/useUnifiedUser';
import { useUserRole } from '@/hooks/useUserRole';
import { shopNotificationService } from '@/services/shopNotificationService';
import { toast } from 'sonner';

const Shop = () => {
  const isMobile = useIsMobile();
  const { user } = useUnifiedUser();
  const { permissions } = useUserRole();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscriberCount, setSubscriberCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const isAdmin = permissions.role === 'admin';

  useEffect(() => {
    if (user) {
      checkSubscription();
      if (isAdmin) {
        loadSubscriberCount();
      }
    }
  }, [user, isAdmin]);

  const checkSubscription = async () => {
    if (!user) return;
    const subscribed = await shopNotificationService.isSubscribed(user.id, user.userType);
    setIsSubscribed(subscribed);
  };

  const loadSubscriberCount = async () => {
    const count = await shopNotificationService.getSubscriberCount();
    setSubscriberCount(count);
  };

  const handleNotifyMe = async () => {
    if (!user) {
      toast.error('Please log in to get notified');
      return;
    }
    
    setIsLoading(true);
    const result = await shopNotificationService.subscribeToLaunch(
      user.id, 
      user.userType, 
      user.username || 'User',
      user.email
    );
    
    if (result.success) {
      setIsSubscribed(true);
      toast.success("You're on the list! We'll notify you when we launch 🎉");
    } else {
      toast.error(result.error || 'Failed to subscribe');
    }
    setIsLoading(false);
  };

  const handleNotifyAll = async () => {
    if (!user || !isAdmin) return;
    
    setIsLoading(true);
    const result = await shopNotificationService.notifyAllSubscribers(user.id);
    
    if (result.success) {
      toast.success(`Notified ${result.notifiedCount} users about shop launch! 🚀`);
      setSubscriberCount(0);
    } else {
      toast.error('Failed to send notifications');
    }
    setIsLoading(false);
  };
  
  return (
    <div className="flex flex-col min-h-screen bg-black w-full">
      {!isMobile && <Header />}
      {isMobile && <MobileHeader />}
      
      <main className="flex-grow mt-24 py-6 md:py-12 px-4 md:px-6 pb-20 md:pb-6 w-full">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4 flex items-center justify-center gap-3">
              <ShoppingBag className="h-8 w-8 text-sports-green" />
              Players Lockeroom Shop
              <span className="px-3 py-1 bg-sports-green text-black text-sm font-bold rounded-full">
                COMING SOON
              </span>
            </h1>
            <p className="text-gray-300 text-lg">
              Your one-stop shop for sports gear, equipment, and exclusive merchandise
            </p>
          </div>

          {/* Big Players Section */}
          <Card className="bg-gradient-to-r from-purple-900/80 to-pink-900/80 border-purple-400/50 mb-8">
            <CardHeader>
              <CardTitle className="text-white text-center flex items-center justify-center gap-3">
                <Crown className="h-8 w-8 text-purple-300" />
                Big Players Exclusive Section
                <span className="px-3 py-1 bg-purple-600 text-white text-sm font-bold rounded-full">
                  VIP
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <p className="text-gray-100 text-lg font-medium">
                Exclusive merchandise and perks for users who have referred 100+ people
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-purple-800/60 border border-purple-400/40 rounded-lg p-4 backdrop-blur-sm">
                  <h4 className="font-semibold text-purple-200 mb-2">Premium Gear</h4>
                  <p className="text-sm text-gray-200">Exclusive Big Player branded equipment and apparel</p>
                </div>
                <div className="bg-purple-800/60 border border-purple-400/40 rounded-lg p-4 backdrop-blur-sm">
                  <h4 className="font-semibold text-purple-200 mb-2">VIP Discounts</h4>
                  <p className="text-sm text-gray-200">Special pricing on all items with priority access</p>
                </div>
                <div className="bg-purple-800/60 border border-purple-400/40 rounded-lg p-4 backdrop-blur-sm">
                  <h4 className="font-semibold text-purple-200 mb-2">Limited Edition</h4>
                  <p className="text-sm text-gray-200">Access to exclusive Big Player collection items</p>
                </div>
              </div>
              <p className="text-purple-200 font-semibold text-lg">
                Refer 100 people to unlock Big Player status and access this exclusive section!
              </p>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader className="text-center">
                <Star className="h-8 w-8 text-sports-green mx-auto mb-2" />
                <CardTitle className="text-white">Premium Equipment</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-300 text-center">
                  High-quality sports equipment from top brands
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gray-800 border-gray-700">
              <CardHeader className="text-center">
                <Truck className="h-8 w-8 text-sports-green mx-auto mb-2" />
                <CardTitle className="text-white">Fast Delivery</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-300 text-center">
                  Quick and reliable shipping to your location
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gray-800 border-gray-700">
              <CardHeader className="text-center">
                <CreditCard className="h-8 w-8 text-sports-green mx-auto mb-2" />
                <CardTitle className="text-white">Secure Payment</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-300 text-center">
                  Safe and secure payment processing
                </p>
              </CardContent>
            </Card>
          </div>

          <Card className="bg-gray-800 border-sports-green/50">
            <CardHeader>
              <CardTitle className="text-white text-center text-xl font-bold">Get Notified When We Launch!</CardTitle>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <p className="text-white mb-4 text-lg">
                Be the first to know when our shop goes live. We're working hard to bring you 
                the best selection of sports gear, team merchandise, and exclusive Players Lockeroom items.
              </p>
              
              {/* Notify Me Button */}
              {user && !isSubscribed ? (
                <Button 
                  onClick={handleNotifyMe} 
                  disabled={isLoading}
                  className="bg-sports-green hover:bg-sports-green/80 text-black font-bold px-8 py-3 text-lg"
                >
                  <Bell className="mr-2 h-5 w-5" />
                  {isLoading ? 'Subscribing...' : 'Notify Me'}
                </Button>
              ) : user && isSubscribed ? (
                <div className="flex items-center justify-center gap-2 text-sports-green py-3">
                  <BellRing className="h-6 w-6 animate-pulse" />
                  <span className="font-semibold text-lg">You're on the list! We'll notify you when we launch.</span>
                </div>
              ) : (
                <p className="text-gray-400 py-2">Log in to get notified when we launch</p>
              )}
              
              {/* Admin Notify All Button */}
              {isAdmin && (
                <div className="mt-6 p-4 bg-purple-900/30 border border-purple-500/50 rounded-lg">
                  <p className="text-purple-200 mb-3 font-medium">
                    Admin: {subscriberCount} user{subscriberCount !== 1 ? 's' : ''} waiting for launch notification
                  </p>
                  <Button 
                    onClick={handleNotifyAll}
                    disabled={isLoading || subscriberCount === 0}
                    className="bg-purple-600 hover:bg-purple-700 text-white font-bold px-6 py-2"
                  >
                    <Megaphone className="mr-2 h-5 w-5" />
                    {isLoading ? 'Sending...' : `Notify All (${subscriberCount} users)`}
                  </Button>
                </div>
              )}
              
              <p className="text-sports-green font-bold text-xl pt-2">
                Coming Soon - Stay Tuned!
              </p>
            </CardContent>
          </Card>
        </div>
      </main>
      
      {!isMobile && <Footer />}
      {isMobile && <MobileBottomNav />}
    </div>
  );
};

export default Shop;
